import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import dynamic from "next/dynamic";

import isEmpty from "lodash/isEmpty";
import get from "lodash/get";

import { useAuth0V2 } from "@stadium/services/auth0";
import { useQuery } from "@stadium/services/apollo-client";

import useRouter from "@stadium/hooks/lib/use-custom-route";

import AuthLogin from "@components/auth/buttons/login";
import { InternalButton } from "@sharedComponents/buttons";

import { getCartOrder, getCartOrderLineItems } from "@store/selectors/cart";
import { getCountry } from "@selectors/country";

import {
  getCurrentQtyOfProductInCart,
  mapDietaryRestrictions,
  getCleanedAttributes,
  getColorAndSizeFallbacks,
} from "@helpers/orders/cart";
import { getUpdatedLocalCartQuantity } from "@helpers/products/tile";

import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";
import useBatchPopulateItems from "@hooks/cart/use-batch-populate-items";

import { useProductDetailContext } from "@contexts/product-detail-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useStoreContext } from "@components/store/provider";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { getProductType, getProductCategory } from "@helpers/products/types";

import { useSendItemsContext } from "@contexts/send-items-context";

import { getIsLockerShop, getStoreExternalId } from "@selectors/store";

import { FetchDigitalAssetsDocument } from "@stadium/shared/src/graphql/operations";

import { ASSET_KINDS } from "@stadium/shared/constants/logo";
import { ITEM_PRODUCT_TYPES, ORDER_TYPES } from "@constants/orders/cart";
import { getCartRoute } from "@constants/routes";
import { ON_DEMAND_PRODUCT, LOCKER_PRODUCT } from "@constants/products/detail";
import {
  ANALYTICS_TARGET_ADD_TO_BAG,
  ANALYTICS_TARGET_ADDED_TO_BAG,
} from "@constants/analytics/shop";
import { ANALYTICS_EVENT_ACTION_VIEWED } from "@stadium/services/analytics/constants/default";

const AddLogoModal = dynamic(() => import("@stadium/shared/src/components/add-logo-modal"));

const emptyArr = [];
const getVariantPresentation = (variants, sizeId) =>
  variants.find((s) => String(s.id) === String(sizeId))?.presentation;

const isValidLockerMapping = (hasSize, isMyself, mapping) =>
  hasSize && isMyself && !isEmpty(mapping);

const getLockerVariants = (isColor, variant, data) =>
  isColor ? variant?.variants || [] : Object.values(data || {});

const collectLockerSizesFromMappingEntries = (variants, sizeQtyMapping) => {
  const result = [];
  for (const [sizeId, { qty }] of Object.entries(sizeQtyMapping)) {
    const quantity = Number.parseInt(qty, 10);
    if (quantity <= 0) continue;
    const presentation = getVariantPresentation(variants, sizeId);
    if (!presentation) continue;
    result.push({ value: presentation, quantity });
  }
  return result;
};

const getLockerSizesFromMapping = ({
  hasSizeOptions,
  isSendToMyself,
  sizeQtyMapping,
  isColorPresent,
  variant,
  variantsData,
}) => {
  if (!isValidLockerMapping(hasSizeOptions, isSendToMyself, sizeQtyMapping)) return [];

  const variants = getLockerVariants(isColorPresent, variant, variantsData);

  return collectLockerSizesFromMappingEntries(variants, sizeQtyMapping);
};

const getLockerRawSizes = (hasExistingSizes, mapping, size, qty) => {
  if (hasExistingSizes) return mapping;
  if (!size) return undefined;
  return [{ value: size, quantity: qty || 1 }];
};

const getLockerRawAttributes = ({
  color,
  sizesFromMapping,
  hasExistingSizes,
  size,
  totalQty,
  preferences,
  dietaryRestrictions,
}) => {
  const preferenceStr = String(preferences ?? "").trim();
  return {
    colors: color ? [color] : undefined,
    sizes: getLockerRawSizes(hasExistingSizes, sizesFromMapping, size, totalQty),
    dietaryPreference: preferenceStr || undefined,
    dietaryRestrictions: mapDietaryRestrictions(dietaryRestrictions),
  };
};

const getLockerAddToBagSelectedAttributes = (params) => {
  const {
    hasSizeOptions,
    isSendToMyself,
    sizeQtyMapping,
    isColorPresent,
    variant,
    variantsData,
    variantSelected,
    preferences,
    totalQty,
    dietaryRestrictions = [],
  } = params;

  const sizesFromMapping = getLockerSizesFromMapping({
    hasSizeOptions,
    isSendToMyself,
    sizeQtyMapping,
    isColorPresent,
    variant,
    variantsData,
  });

  const hasExistingSizes = !isEmpty(sizesFromMapping);
  const { color, size } = getColorAndSizeFallbacks({
    variant,
    variantSelected,
    hasExistingSizes,
  });

  const rawAttributes = getLockerRawAttributes({
    color,
    sizesFromMapping,
    hasExistingSizes,
    size,
    totalQty,
    preferences,
    dietaryRestrictions,
  });

  return getCleanedAttributes(rawAttributes);
};

const AddToBagLockerBtn = ({
  product,
  totalQty,
  sizeQtyMapping,
  variant,
  btnLabel = "Add to Bag",
  onSuccessCallback = () => {},
  btnParentClass = "",
  isDisabled = false,
  variantSelected,
  price,
  hasSizeOptions,
  isColorPresent,
  variantsData,
  totalPrice,
  isColorAndSizeSelectionAvailable = true,
  preferences = "",
  isCustomizable,
  restrictions = emptyArr,
}) => {
  const { isAuthenticated } = useAuth0V2();
  const router = useRouter();
  const { storeBasePath } = useStoreContext();
  const { toggleAddedToBagModal, isColorSelected, setColorSelectionError, editProductIds } =
    useProductDetailContext();
  const selectedCountry = useSelector(getCountry);
  const isLockerShop = useSelector(getIsLockerShop);
  const { addItemInCart, loading } = useBatchPopulateItems({
    type: isLockerShop ? ORDER_TYPES.lead : ORDER_TYPES.organizer,
  });
  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const { addBulkItemsInLocalCart } = usePopulateLocalItems();
  const storeId = useSelector(getStoreExternalId);
  const order = useSelector(getCartOrder);
  const lineItems = useSelector(getCartOrderLineItems);

  const withAnalytics = useWithAnalytics();

  const logoCustomizationData = useMemo(() => {
    const lineItemCustomization = editProductIds
      .map((id) => lineItems.find(({ id: lineItemId }) => id === lineItemId)?.lineItemCustomization)
      ?.filter(Boolean);

    const logoIds = lineItemCustomization
      ?.map((lineItemCustomization) => lineItemCustomization?.logoId)
      ?.filter(Boolean);
    return {
      logoIds,
      lineItemCustomization,
    };
  }, [lineItems, editProductIds]);

  const disabled = loading || !totalQty || isDisabled;
  const { isSendToMyself } = useSendItemsContext();

  const [showAddLogoModal, setShowAddLogoModal] = useState(false);

  const toggleAddLogoModal = () => setShowAddLogoModal(!showAddLogoModal);

  const { data: digitalAssetsData, loading: isDigitalAssetsLoading } = useQuery(
    FetchDigitalAssetsDocument,
    {
      variables: {
        kind: ASSET_KINDS.logo,
        ids: logoCustomizationData?.logoIds,
      },
      skip: !isLockerShop || logoCustomizationData?.logoIds?.length === 0,
    },
  );

  const {
    intent,
    name,
    productCatalogSlug,
    id,
    bulkAvailable,
    optionTypes,
    variantsIncludingMaster,
    vendorByCountry,
    masterVariant,
    currentCatalogExternalStoreId,
    brand,
    lockerProduct = false,
    hasTierPricing,
  } = product;

  const onAddToBagSuccess = () => {
    toggleAddedToBagModal({
      product,
      quantity: totalQty,
      variant,
      totalPrice,
      isCheckoutBtnRequired: isSendToMyself,
    });
    onSuccessCallback();
  };

  const handleAddButton = () => {
    if (isColorAndSizeSelectionAvailable && !isColorSelected) {
      setColorSelectionError(true);
      return;
    }

    const selected_attributes = getLockerAddToBagSelectedAttributes({
      variant,
      variantSelected,
      hasSizeOptions,
      isSendToMyself,
      isColorPresent,
      sizeQtyMapping,
      variantsData,
      preferences,
      totalQty,
      dietaryRestrictions: restrictions,
    });

    withAnalytics(undefined, {
      target: ANALYTICS_TARGET_ADD_TO_BAG,
      track: true,
      eventProperties: {
        product_id: id,
        quantity: totalQty,
        selected_attributes,
        product_type: getProductType(product),
        category: getProductCategory(product),
      },
    })();

    if (isCustomizable) {
      toggleAddLogoModal();
      return;
    }

    addToBag();
  };
  const addPreviewModeProduct = (payload) => addBulkItemsInLocalCart(payload);

  const isVariantChanged = (productsInCart) => {
    if (!isEmpty(variant.variants)) {
      return variant.variants.every(({ externalId }) => !productsInCart.includes(externalId));
    }
    if (!hasSizeOptions) return variant.externalId && variant.externalId !== productsInCart[0];
  };

  const deleteExistingVariantIfUpdated = (payload, storeCatalogId) => {
    // below condition check if user select another color while updating a product
    // if yes we will delete existing product by setting quantity as 0
    const productsIncart = editProductIds
      .map((id) => lineItems.find(({ id: lineItemId }) => id === lineItemId)?.productId?.toString())
      ?.filter(Boolean);
    if (!isEmpty(editProductIds) && !isEmpty(productsIncart) && isVariantChanged(productsIncart)) {
      payload = productsIncart.map((id) => ({
        productId: id,
        quantity: 0,
        productType: ITEM_PRODUCT_TYPES.product,
        storeId: storeCatalogId,
      }));
    }
    return payload;
  };

  const getStoreCatalogId = () => {
    let storeCatalogId = product?.currentCatalogExternalStoreId;
    if (!storeCatalogId && isLockerShop) {
      storeCatalogId = storeId;
    }
    return storeCatalogId;
  };

  const addToBag = (addLogoProps = {}) => {
    let payload = [];
    const storeCatalogId = getStoreCatalogId();
    const zoneId = selectedCountry?.zoneId || "";
    const itemType = lockerProduct ? LOCKER_PRODUCT : ON_DEMAND_PRODUCT;
    payload = deleteExistingVariantIfUpdated(payload, storeCatalogId);
    if (hasSizeOptions && isSendToMyself) {
      payload.push(
        ...Object.keys(sizeQtyMapping).map((sizeId) => {
          const { qty, storePrice, lineItemId } = sizeQtyMapping[sizeId];
          const quantity = Number.parseInt(qty, 10);
          const variants = isColorPresent ? variant.variants : Object.values(variantsData);
          const sizeVariant = variants.find(({ id: variantId }) => variantId == sizeId);
          const productId = sizeVariant.externalId;
          return {
            ...addLogoProps,
            productId,
            storePrice,
            quantity:
              getUpdatedLocalCartQuantity({
                quantity,
                isGuestOrPreviewMode,
                productId,
                storeId: storeCatalogId,
                isEditMode: !isEmpty(editProductIds),
                lineItems,
              }) +
              (order.id && !lineItemId
                ? getCurrentQtyOfProductInCart({
                    order,
                    productId,
                    currentCatalogExternalStoreId: storeCatalogId,
                    zoneId,
                  })
                : 0),
            storeId: storeCatalogId,
            vendorId: Number(sizeVariant?.vendorId),
            variant: {
              ...sizeVariant,
              product: {
                id,
                catalogStoreId: storeCatalogId,
                currentCatalogExternalStoreId,
                bulkAvailable,
                intent,
                optionTypes,
                variantsIncludingMaster,
                vendorByCountry,
                masterVariant,
                brand,
                hasTierPricing,
              },
              name,
              slug: productCatalogSlug,
              restaurantName: product?.brand?.displayName,
            },
            shippedSeparately: product?.shippedSeparately || false,
            zoneId,
            itemType,
          };
        }),
      );
    } else {
      const externalId = variantSelected?.externalId || variant?.externalId;
      payload.push({
        ...addLogoProps,
        productId: externalId,
        storePrice: price,
        quantity:
          totalQty +
          (order.id
            ? getCurrentQtyOfProductInCart({
                order,
                productId: externalId,
                currentCatalogExternalStoreId: storeCatalogId,
                editProductId: editProductIds[0],
                zoneId,
              })
            : 0),
        storeId: storeCatalogId,
        vendorId: product.vendorByCountry?.externalId,
        variant: {
          ...variant,
          product: {
            id,
            name,
            slug: productCatalogSlug,
            catalogStoreId: storeCatalogId,
            currentCatalogExternalStoreId,
            bulkAvailable,
            intent,
            optionTypes,
            variantsIncludingMaster,
            vendorByCountry,
            masterVariant,
            brand,
            hasTierPricing,
          },
          name,
          slug: productCatalogSlug,
          restaurantName: product?.brand?.displayName,
        },
        shippedSeparately: product?.shippedSeparately || false,
        isMaster: variantSelected?.isMaster || false,
        zoneId,
        itemType,
      });
    }
    (isGuestOrPreviewMode
      ? addPreviewModeProduct(payload)
      : addItemInCart(
          payload.map(({ productId, storePrice, quantity, storeId, vendorId }) => ({
            product_id: productId,
            storePrice,
            quantity,
            product_type: ITEM_PRODUCT_TYPES.product,
            store_id: storeId,
            vendor_id: vendorId,
            zone_id: zoneId,
            item_type: itemType,
            dietary_preference: isSendToMyself ? preferences : null,
            ...addLogoProps,
          })),
        )
    ).then(() => {
      if (!isEmpty(editProductIds)) router.push(getCartRoute({ storeBasePath }));
      else {
        const selected_attributes = getLockerAddToBagSelectedAttributes({
          variant,
          variantSelected,
          hasSizeOptions,
          isSendToMyself,
          isColorPresent,
          sizeQtyMapping,
          variantsData,
          preferences,
          totalQty,
          dietaryRestrictions: restrictions,
        });
        withAnalytics(onAddToBagSuccess, {
          target: ANALYTICS_TARGET_ADDED_TO_BAG,
          action: ANALYTICS_EVENT_ACTION_VIEWED,
          track: true,
          eventProperties: {
            product_id: id,
            quantity: totalQty,
            selected_attributes,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        })();
      }
    });
  };

  if (isLockerShop && !isAuthenticated)
    return (
      <AuthLogin className={btnParentClass}>
        <InternalButton styleType="primary">{btnLabel}</InternalButton>
      </AuthLogin>
    );

  const renderAddLogoModal = () => {
    const commonAddLogoProps = {
      productName: name,
      closeModal: toggleAddLogoModal,
      addToBag,
    };

    const primaryLogo = get(digitalAssetsData, "digitalAssets.nodes[0]", null);
    const lineItemCustomization = get(logoCustomizationData, "lineItemCustomization[0]", null);

    return (
      <AddLogoModal
        {...commonAddLogoProps}
        {...(primaryLogo &&
          lineItemCustomization && { editLogoData: { primaryLogo, lineItemCustomization } })}
      />
    );
  };

  return (
    <>
      <div className={btnParentClass}>
        <InternalButton
          styleType="primary"
          onClick={handleAddButton}
          isLoading={loading || isDigitalAssetsLoading}
          disabled={disabled}
        >
          {btnLabel}
        </InternalButton>
      </div>
      {showAddLogoModal && renderAddLogoModal()}
    </>
  );
};

export default AddToBagLockerBtn;
